import React, { useState, useEffect } from 'react';
import { History, User, Calendar } from 'lucide-react';
import { importService } from '../../../services/importService';

const LeadAuditTrail = ({ leads }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedLead) {
      loadHistory(selectedLead?.id);
    }
  }, [selectedLead]);

  const loadHistory = async (leadId) => {
    try {
      setLoading(true);
      const historyData = await importService?.getLeadHistory(leadId);
      setHistory(historyData || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'created':
        return 'bg-blue-100 text-blue-800';
      case 'status_change':
        return 'bg-purple-100 text-purple-800';
      case 'assignment':
        return 'bg-green-100 text-green-800';
      case 'updated':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Lead Audit Trail</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Selection */}
        <div className="lg:col-span-1">
          <h3 className="font-semibold text-gray-900 mb-4">Select Lead</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {leads?.map((lead) => (
              <button
                key={lead?.id}
                onClick={() => setSelectedLead(lead)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedLead?.id === lead?.id
                    ? 'border-blue-600 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <p className="font-medium text-gray-900">{lead?.nombre}</p>
                <p className="text-sm text-gray-600">{lead?.email}</p>
              </button>
            ))}
          </div>
        </div>

        {/* History Timeline */}
        <div className="lg:col-span-2">
          {!selectedLead ? (
            <div className="text-center py-12">
              <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Select a lead to view audit trail</p>
            </div>
          ) : loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading history...</p>
            </div>
          ) : (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">
                History for {selectedLead?.nombre}
              </h3>
              
              {history?.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No history available</p>
              ) : (
                <div className="space-y-4">
                  {history?.map((record) => (
                    <div key={record?.id} className="border-l-4 border-blue-600 pl-4 py-2">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            getChangeTypeColor(record?.changeType)
                          }`}>
                            {record?.changeType}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {record?.fieldChanged}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          {new Date(record?.changedAt)?.toLocaleString()}
                        </div>
                      </div>
                      
                      {record?.oldValue && record?.newValue && (
                        <div className="text-sm text-gray-600 mt-2">
                          <span className="line-through text-red-600">{record?.oldValue}</span>
                          <span className="mx-2">→</span>
                          <span className="text-green-600">{record?.newValue}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                        <User className="w-4 h-4" />
                        <span>Changed by: {record?.changedBy}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadAuditTrail;